'use client';

import React, { useState, useMemo } from 'react';
import { 
  Wand2, Calendar, CheckCircle2, 
  Rocket, Search, FileText, RefreshCw,
  Sparkles, Layers, ArrowRight, Info, Zap, ChevronRight, Check, Loader
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import aiPlannerService from '../../services/aiPlannerService';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';

import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
const AiPlannerPage = () => {
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  const handleGenerate = async (e) => {
    if (e) e.preventDefault();
    if (!topic.trim()) return;

    setLoading(true);
    setError(null);
    setPlan(null);

    try {
      const res = await aiPlannerService.generatePlan(topic);
      if (res.success) {
        setPlan(res.plan);
      } else {
        setError(res.message || 'Failed to generate plan');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!plan || !plan.tasks) return;
    const confirmed = await alertService.confirm('Create these tasks?', 'The generated tasks will be added to your CRM tasks.', 'question', 'Create Tasks');
    if (!confirmed) return;

    setApplying(true);
    try {
      const res = await aiPlannerService.applyPlan(plan.tasks);
      if (res.success) {
        alertService.success('Success', 'Campaign plan applied to CRM');
        setPlan(null);
        setTopic('');
      }
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || err.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className={PAGE}>
      
      {/* Header Area */}
      <PageHeader
        title="AI CRM Planner"
        subtitle="Generate a weekly sales plan with AI."
        icon={Zap}
      />

      {/* Input Section */}
      <div className="sf-card p-8 bg-white border-l-4 border-[#0176D3]">
        <div className="w-full space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-[#0176D3]" />
            <span className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">AI Sales Planner</span>
          </div>
          <h2 className="text-2xl font-bold text-[#080707]">Define Your Strategic Objective</h2>
          <div className="flex flex-col md:flex-row gap-4">
            <input 
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={loading}
              placeholder="e.g. Scaling enterprise software sales with hyper-targeted WhatsApp automation…"
              className="flex-1 h-9 px-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[14px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
            />
            <button 
              onClick={handleGenerate}
              disabled={loading || !topic.trim()}
              className="h-12 px-8 bg-[#0176D3] text-white rounded font-bold text-[14px] hover:bg-[#014486] transition-all flex items-center gap-2 disabled:opacity-50 whitespace-nowrap shrink-0"
            >
              {loading ? <Loader size={18} className="animate-spin" /> : <Rocket size={18} />}
              {loading ? 'Analyzing…' : 'Generate Plan'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-[#FDE7E9] border border-[#F6A3AB] rounded text-[#BA0517] text-sm font-bold flex items-center gap-3">
          <Info size={18} /> {error}
        </div>
      )}

      {/* Plan Results */}
      <AnimatePresence>
        {plan && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-4">
            
            {/* Sidebar Summary */}
            <div className="lg:col-span-4 space-y-6">
              <div className="sf-card overflow-hidden">
                <div className="p-6 bg-[#0176D3] text-white">
                  <h3 className="text-xl font-bold mb-2">{plan.planTitle}</h3>
                  <p className="text-[13px] opacity-90 leading-relaxed italic">"{plan.summary}"</p>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-[#D8DDE6]">
                    <span className="text-[11px] font-bold text-[#706E6B] uppercase">Cycle Duration</span>
                    <span className="sf-badge sf-badge-neutral">30 DAYS</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-[#D8DDE6]">
                    <span className="text-[11px] font-bold text-[#706E6B] uppercase">Tactical Modules</span>
                    <span className="sf-badge sf-badge-neutral">{plan.tasks.length} SECURED</span>
                  </div>
                  <button 
                    onClick={handleApply}
                    disabled={applying}
                    className="w-full h-12 bg-[#2E844A] text-white rounded font-bold text-[13px] hover:bg-[#1E5D32] transition-all shadow-[0_2px_8px_rgba(0,0,0,0.16)] mt-4 flex items-center justify-center gap-2"
                  >
                    {applying ? <Loader size={16} className="animate-spin" /> : <Check size={18} />}
                    {applying ? 'Applying…' : 'Apply to CRM'}
                  </button>
                </div>
              </div>
            </div>

            {/* Roadmap Stream */}
            <div className="lg:col-span-8 space-y-10">
              {[1, 2, 3, 4].map((week) => {
                const weekTasks = plan.tasks.filter(t => t.week === week);
                if (weekTasks.length === 0) return null;

                return (
                  <div key={week} className="relative pl-8 border-l-2 border-[#D8DDE6]">
                    <div className="absolute -left-[11px] top-0 w-5 h-5 rounded-full bg-white border-4 border-[#0176D3]" />
                    <h4 className="text-[11px] font-bold text-[#706E6B] uppercase tracking-[0.2em] mb-6">Week {week}</h4>
                    
                    <div className="space-y-6">
                      {weekTasks.map((task, idx) => (
                        <div key={idx} className="sf-card p-6 hover:border-[#0176D3] transition-all group">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h5 className="text-[15px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors">{task.title}</h5>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="sf-badge sf-badge-neutral lowercase">{task.type}</span>
                                <span className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">{task.priority} Priority</span>
                              </div>
                            </div>
                            <div className="w-8 h-8 rounded bg-[#F4F6F9] flex items-center justify-center text-[#706E6B] group-hover:text-[#0176D3] transition-all">
                              <ArrowRight size={16} />
                            </div>
                          </div>
                          
                          <p className="text-[13px] text-[#706E6B] leading-relaxed mb-6">{task.description}</p>
                          
                          <div className="bg-[#F3F2F2] border border-[#D8DDE6] rounded-md p-4 mb-4">
                            <p className="text-[10px] font-bold text-[#0176D3] uppercase tracking-widest mb-2 flex items-center gap-2"><Sparkles size={12} /> Preview</p>
                            <p className="text-[13px] text-[#080707] font-medium leading-relaxed italic">{task.content}</p>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 pt-2">
                            {task.seoKeywords?.map((kw, kIdx) => (
                              <span key={kIdx} className="text-[10px] font-bold text-[#706E6B] bg-white border border-[#D8DDE6] px-2.5 py-1 rounded-full">{kw}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {!plan && !loading && (
        <div className="py-32 flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 rounded-full bg-[#F4F6F9] flex items-center justify-center mb-6">
            <Wand2 size={40} className="text-[#0176D3] opacity-20" />
          </div>
          <h4 className="text-xl font-bold text-[#080707] mb-2 tracking-tight">No plan generated yet</h4>
          <p className="text-[13px] text-[#706E6B] max-w-sm mx-auto font-medium">Input a business objective to generate high-performance strategic directives.</p>
        </div>
      )}
    </div>
  );
};

export default AiPlannerPage;
